import { GoogleAuthProvider, signOut as firebaseSignOut, onAuthStateChanged, signInWithPopup, type User } from 'firebase/auth';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { auth } from '@/services/firebase';

export type AuthStatus = 'loading' | 'unauthenticated' | 'authorized' | 'unauthorized';

interface AuthContextValue {
  user: User | null;
  status: AuthStatus;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  signInInProgress: boolean;
  signInError: string | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const ALLOWED_EMAIL = (import.meta.env.VITE_ALLOWED_EMAIL ?? '').trim().toLowerCase();

function isAllowed(user: User): boolean {
  if (!ALLOWED_EMAIL) return false;
  const email = user.email?.trim().toLowerCase();
  return !!email && email === ALLOWED_EMAIL;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [signInInProgress, setSignInInProgress] = useState(false);
  const [signInError, setSignInError] = useState<string | null>(null);

  useEffect(() => {
    if (import.meta.env.DEV && !ALLOWED_EMAIL) {
      console.warn('[auth] VITE_ALLOWED_EMAIL is not configured — every signed-in user will be marked unauthorized.');
    }
    const unsubscribe = onAuthStateChanged(auth, (fbUser) => {
      setUser(fbUser);
      if (!fbUser) {
        setStatus('unauthenticated');
        return;
      }
      setStatus(isAllowed(fbUser) ? 'authorized' : 'unauthorized');
    });
    return unsubscribe;
  }, []);

  const signInWithGoogle = useCallback(async () => {
    setSignInInProgress(true);
    setSignInError(null);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(auth, provider);
    } catch (err) {
      const code = err && typeof err === 'object' && 'code' in err ? String((err as { code: unknown }).code) : '';
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        setSignInError('Sign-in cancelled.');
      } else {
        const message = err instanceof Error ? err.message : 'Sign-in failed.';
        setSignInError(message);
      }
    } finally {
      setSignInInProgress(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    setSignInError(null);
    await firebaseSignOut(auth);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, status, signInWithGoogle, signOut, signInInProgress, signInError }),
    [user, status, signInWithGoogle, signOut, signInInProgress, signInError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
